import {
  ConnectedPosition,
  Overlay,
  ScrollStrategy
} from '@angular/cdk/overlay';
import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnDestroy
} from '@angular/core';
import { CustomControlValueAccessor } from '../custom-control-value-accessor/custom-control-value-accessor';
import {
  ALTURA_MAXIMA_DROPDOWN,
  DROPDOWN_OVERLAY_POSITIONS,
  fecharAoRolarAncestral,
  ordenarPosicoesPeloEspacoDisponivel
} from '../dom-events/dropdown-overlay.util';

@Directive()
export abstract class DropdownOverlayBase<T>
  extends CustomControlValueAccessor<T>
  implements OnDestroy {
  @Input() variante: 'filtro' | undefined;
  @Input() panelClass: string | undefined;
  @Input() icon: string | undefined;

  isOpen = false;
  larguraDoTrigger = 0;
  overlayPositions: ConnectedPosition[] = DROPDOWN_OVERLAY_POSITIONS;
  readonly overlayMaxHeight = ALTURA_MAXIMA_DROPDOWN;
  readonly scrollStrategy: ScrollStrategy;

  private pararDeObservarScroll?: () => void;

  constructor(
    protected elementRef: ElementRef<HTMLElement>,
    protected ngZone: NgZone,
    protected changeDetectorRef: ChangeDetectorRef,
    overlay: Overlay
  ) {
    super();
    this.scrollStrategy = overlay.scrollStrategies.close();
  }

  protected abstract get overlayPanelClass(): string;

  protected abstract get triggerElement(): HTMLElement;

  protected abstract aoFecharPorInteracaoExterna(): void;

  get exibirIcone(): boolean {
    return !!this.icon;
  }

  get overlayPanelClasses(): string[] {
    const classes =
      this.variante === 'filtro'
        ? [this.overlayPanelClass, `${this.overlayPanelClass}--filtro`]
        : [this.overlayPanelClass];

    return this.panelClass ? [...classes, this.panelClass] : classes;
  }

  onOverlayDetach(): void {
    if (this.isOpen) {
      this.aoFecharPorInteracaoExterna();
    }
  }

  onOverlayOutsideClick(event: MouseEvent): void {
    const alvo = event.target as Element;

    if (this.elementRef.nativeElement.contains(alvo)) {
      return;
    }

    if (alvo?.closest?.(`.${this.overlayPanelClass}`)) {
      return;
    }

    this.aoFecharPorInteracaoExterna();
  }

  protected alternarAberturaDoDropdown(): void {
    if (this.disabled) {
      return;
    }

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.prepararOverlay();
    }
  }

  protected onDisabledStateChange(isDisabled: boolean): void {
    if (isDisabled && this.isOpen) {
      this.aoFecharPorInteracaoExterna();
    }
  }

  protected prepararOverlay(): void {
    const trigger = this.triggerElement;

    this.larguraDoTrigger = trigger.getBoundingClientRect().width;
    this.overlayPositions = ordenarPosicoesPeloEspacoDisponivel(
      trigger,
      this.overlayMaxHeight
    );
    this.observarScroll();
  }

  protected encerrarObservacaoDeScroll(): void {
    this.pararDeObservarScroll?.();
  }

  private observarScroll(): void {
    this.pararDeObservarScroll = fecharAoRolarAncestral(
      this.elementRef.nativeElement,
      this.overlayPanelClass,
      this.ngZone,
      () => {
        this.aoFecharPorInteracaoExterna();
        this.changeDetectorRef.markForCheck();
      }
    );
  }

  ngOnDestroy(): void {
    this.encerrarObservacaoDeScroll();
  }
}
